<template>
  <div class="geofence-list-bar hidden flex-wrap items-center gap-2 sm:flex">
    <GpxMultiSelect
      v-model="placeTypeFilter"
      :options="placeTypeFilterOptions"
      option-label="label"
      option-value="value"
      placeholder="Place type"
      size="small"
      :max-selected-labels="2"
      class="min-w-[160px]"
      show-select-all
      filter
      filter-placeholder="Search"
    >
      <template #option="{ option }">
        <span :class="{ 'pl-4': option.isCustom }">{{ option.label }}</span>
      </template>
    </GpxMultiSelect>
    <GpxMultiSelect
      v-model="ownershipFilter"
      :options="OWNERSHIP_FILTER_OPTIONS"
      option-label="label"
      option-value="value"
      placeholder="Ownership"
      size="small"
      :max-selected-labels="2"
      class="min-w-[140px]"
      show-select-all
    />
    <div class="ml-auto flex items-center gap-2">
      <Button
        title="Download Geofence Report"
        size="sm"
        variant="tertiary"
        icon="ArrowDownTrayIcon"
        class="!h-[34px] w-1/2 sm:w-auto"
        @click="downloadGeofences"
      ></Button>
      <DatePicker
        v-model="date"
        selection-mode="range"
        :max-date="new Date()"
        size="small"
        date-format="yy-mm-dd"
        placeholder="Select range"
        show-button-bar
        show-icon
        :show-on-focus="false"
        icon-display="input"
        class="w-[220px]"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { GpxMultiSelect } from '@gpx-spa/ui';
import DatePicker from 'primevue/datepicker';
import {
  NO_VALUE,
  NO_VALUE_LABEL,
  OWNERSHIP_FILTER_OPTIONS,
} from '@/config/geofenceTaxonomy';
import { usePlaceTypes } from '@/composables/usePlaceTypes';
import * as events from '@/events';

const store = useStore();
const toast = useToast();

const date = ref(null);

const accountId = store.getters['auth/accountId'];

const { options: placeTypeOptions, parents: placeTypeParents } =
  usePlaceTypes();
const placeTypeFilterOptions = computed(() => [
  { value: NO_VALUE, label: NO_VALUE_LABEL },
  ...placeTypeOptions.value,
]);
watch(
  placeTypeParents,
  (parents) => store.dispatch('geofence/setPlaceTypeParents', parents),
  { immediate: true },
);

const placeTypeFilter = computed({
  get: () => store.getters['geofence/placeTypeFilter'],
  set: (values) => store.dispatch('geofence/setPlaceTypeFilter', values),
});
const ownershipFilter = computed({
  get: () => store.getters['geofence/ownershipFilter'],
  set: (values) => store.dispatch('geofence/setOwnershipFilter', values),
});

const downloadGeofences = () => {
  events.trigger(events.names.GEOFENCE_LIST_DOWNLOADED);

  store
    .dispatch('geofence/downloadAll', {
      accountId,
      params: {
        download: true,
        offset: new Date().getTimezoneOffset(),
        date_from: date.value[0],
        date_to: date.value[1],
      },
    })
    .then(() => {
      store.dispatch('modal/open', {
        name: 'DownloadConfirmationModal',
        params: {
          title: 'Geofence Events',
          body: 'Your CSV is being generated. Once ready, you will receive an email notification with the download link',
        },
      });
    })
    .catch((e) => {
      toast.error(e.response?.data?.message || 'An error occurred');
    });
};
</script>

<style scoped>
/* Pin every interactive element on the row to the same height as
   PrimeVue size="small" (~34px) so the row reads as one band. */
.geofence-list-bar :deep(.p-multiselect),
.geofence-list-bar :deep(.p-datepicker-input),
.geofence-list-bar :deep(.p-inputtext) {
  height: 34px;
}
</style>
